/**
 * Resolves a material's logistic control unit (FS-241) and converts quantities to/from it.
 *
 * Pure TypeScript on purpose: no Angular, no models-package import. The structural types below
 * are a deliberate subset of `MaterialAttributes` / `OrderDetailAttributes` so this file and its
 * spec stay independent of the schema shape. `getLogisticUnitId` is the only place that knows
 * where `logisticUnitOfMeasureId` lives on the material document.
 */

export type LogisticSlot = 'BASE' | 'PARALLEL' | 'ALTERNAL';

export interface UnitRef {
  _id: string;
  code?: string;
  name?: string;
}

export interface ResolvedLogisticUnit {
  unitId: string;
  code: string;
  name: string;
  /** UMB per 1 logistic unit. */
  factorToBase: number;
  slot: LogisticSlot;
}

export interface MaterialMasterLike {
  _id: string;
  logisticUnitOfMeasureId?: string | null;
  baseUnitOfMeasure?: UnitRef | string;
  alternalUnitOfMeasures?: Array<{
    unitOfMeasure: UnitRef | string;
    conversionFactor?: number;
    quantity?: number;
    equivalentQuantity?: number;
  }>;
}

export interface DetailUnitsLike {
  baseUnitOfMeasure?: UnitRef;
  pararellUnitOfMeasure?: UnitRef;
  soldUnitOfMeasure?: UnitRef;
  pararellConversionFactor?: number;
}

/** Reads the material's configured logistic unit id, or null when not configured. */
export function getLogisticUnitId(material: MaterialMasterLike | null | undefined): string | null {
  const id = material?.logisticUnitOfMeasureId;
  return id ? id : null;
}

function unitRefId(unit: UnitRef | string | null | undefined): string | null {
  if (!unit) return null;
  return typeof unit === 'string' ? unit : (unit._id ?? null);
}

/** A factor that can safely divide/multiply a quantity: finite and strictly positive. */
function isUsableFactor(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0;
}

/**
 * Resolves `{ unitId, code, name, factorToBase, slot }` for a material's logistic unit, or `null`
 * when it is not configured, unresolvable, or its factor is not usable.
 *
 * Resolution order (first match wins):
 * 1. `logisticUnitOfMeasureId` equals the detail's parallel unit id → PARALLEL,
 *    `factorToBase = 1 / pararellConversionFactor` (that detail factor is UMP per UMB).
 * 2. Equals the detail's (or master's) base unit id → BASE, `factorToBase = 1`.
 * 3. Matches an `alternalUnitOfMeasures[]` entry on `unitOfMeasure._id` → ALTERNAL,
 *    `factorToBase = conversionFactor` (already UMB per 1 unit), falling back to
 *    `quantity / equivalentQuantity` when `conversionFactor` is missing.
 * 4. No match → null.
 *
 * An alternal entry whose `unitOfMeasure` is a bare string id (not populated with `code`/`name`)
 * is unusable for display and resolves to null rather than a partial object.
 */
export function resolveLogisticUnit(
  material: MaterialMasterLike | null | undefined,
  detail: DetailUnitsLike,
): ResolvedLogisticUnit | null {
  const logisticId = getLogisticUnitId(material);
  if (!logisticId) return null;

  const pararellUnit = detail?.pararellUnitOfMeasure;
  if (pararellUnit && unitRefId(pararellUnit) === logisticId) {
    const inverseFactor = isUsableFactor(detail.pararellConversionFactor)
      ? 1 / detail.pararellConversionFactor
      : NaN;

    if (!isUsableFactor(inverseFactor)) return null;

    return {
      unitId: logisticId,
      code: pararellUnit.code ?? '',
      name: pararellUnit.name ?? '',
      factorToBase: inverseFactor,
      slot: 'PARALLEL',
    };
  }

  const baseUnit = detail?.baseUnitOfMeasure;
  const masterBaseId = unitRefId(material?.baseUnitOfMeasure);
  if ((baseUnit && unitRefId(baseUnit) === logisticId) || masterBaseId === logisticId) {
    return {
      unitId: logisticId,
      code: baseUnit?.code ?? '',
      name: baseUnit?.name ?? '',
      factorToBase: 1,
      slot: 'BASE',
    };
  }

  const alternalMatch = (material?.alternalUnitOfMeasures ?? []).find(
    (entry) => unitRefId(entry.unitOfMeasure) === logisticId,
  );
  if (!alternalMatch) return null;

  const alternalUnit = alternalMatch.unitOfMeasure;
  if (typeof alternalUnit === 'string' || !alternalUnit.code || !alternalUnit.name) {
    return null;
  }

  const factor = isUsableFactor(alternalMatch.conversionFactor)
    ? alternalMatch.conversionFactor
    : isUsableFactor(alternalMatch.quantity) && isUsableFactor(alternalMatch.equivalentQuantity)
      ? alternalMatch.quantity / alternalMatch.equivalentQuantity
      : NaN;

  if (!isUsableFactor(factor)) return null;

  return {
    unitId: logisticId,
    code: alternalUnit.code,
    name: alternalUnit.name,
    factorToBase: factor,
    slot: 'ALTERNAL',
  };
}

/**
 * Logistic unit worth offering in the edit picker. When the logistic unit IS the base unit the
 * existing "UMB" option already covers it, so the wizard treats the material as not configured.
 */
export function resolveEditableLogisticUnit(
  material: MaterialMasterLike | null | undefined,
  detail: DetailUnitsLike,
): ResolvedLogisticUnit | null {
  const unit = resolveLogisticUnit(material, detail);
  return unit && unit.slot !== 'BASE' ? unit : null;
}

/** Converts a quantity entered in the logistic unit to UMB, rounded to `decimals`. */
export function toBaseQuantity(
  quantity: number,
  unit: Pick<ResolvedLogisticUnit, 'factorToBase'>,
  decimals = 3,
): number {
  return Number((quantity * unit.factorToBase).toFixed(decimals));
}

/** Converts a UMB quantity to the logistic unit for display, rounded to `decimals`. */
export function fromBaseQuantity(
  baseQuantity: number,
  unit: Pick<ResolvedLogisticUnit, 'factorToBase'>,
  decimals = 3,
): number {
  return Number((baseQuantity / unit.factorToBase).toFixed(decimals));
}

/** True when the logistic unit is a genuinely different choice from the detail's sold unit. */
export function isRecommended(unit: ResolvedLogisticUnit | null, detail: DetailUnitsLike): boolean {
  return !!unit && unit.unitId !== detail?.soldUnitOfMeasure?._id;
}
